import React, { useEffect, useState } from 'react';
import { sortBy } from 'lodash';
import CMSArticle from 'src/shared/components/cms-article/CMSArticle';
import { PrimaryButton } from 'src/shared/components/button/Button';
import useSamplingEvent from 'src/app/samples/hooks/useSamplingEvent';
import { useAppSelector, useAppDispatch } from 'src/store';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Select from 'src/shared/components/select/Select';
import { getBatchNumberFromBatchId } from 'src/app/samples/samplesHelpers';
import { selectSamplingKit } from 'src/app/samples/sample-manifest/state/sampleManifestSlice';
import useBoolean from 'src/shared/hooks/useBoolean';
import WarningMessage from 'src/shared/components/warning-message/WarningMessage';
import useSampleManifestAnalytics from 'src/app/samples/sample-manifest/hooks/useSampleManifestAnalytics';
import { CircleAlert as ErrorIcon } from 'lucide-react';
import { getCurrentSampleManifestState } from '../../state/sampleManifestSelector';
import { useLockBatchMutation } from '../../state/api/sampleManifestGraphSlice';

const AmendmentContent = () => {
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const { selectedSamplingEventId } = useAppSelector(getCurrentSampleManifestState);
    const { currentProjectId, amendmentBatches, isBatchSubmittedMoreThan5Days } = useSamplingEvent({
        samplingEventId: selectedSamplingEventId,
    });
    const [lockBatch] = useLockBatchMutation();
    const [selectedBatchId, setSelectedBatchId] = useState('');
    const [isLocked, setIsLocked] = useBoolean(false);
    const { trackSampleManifestHomePageAction } = useSampleManifestAnalytics();

    const amendmentBatchOptions = sortBy(
        amendmentBatches?.map(batch => ({
            value: batch.id,
            label: `Batch ${getBatchNumberFromBatchId(batch.id, selectedSamplingEventId)}`,
        })),
        'value'
    );

    useEffect(() => {
        if (amendmentBatchOptions.length > 0 && !selectedBatchId) {
            setSelectedBatchId(amendmentBatchOptions[0].value);
        }
    }, [amendmentBatchOptions]);

    const handleSelectChange = (batchId: string) => {
        setSelectedBatchId(batchId);
    };

    if (amendmentBatches.length === 0) {
        return null;
    }

    const onAmendSampleBatch = async () => {
        if (!selectedBatchId || !currentProjectId) {
            return;
        }

        setIsLocked.off();

        // Obtain a lock on the batch
        const isLockObtained = await lockBatch({
            projectId: currentProjectId,
            samplingEventId: selectedSamplingEventId,
            batchId: selectedBatchId,
        }).unwrap();

        if (!isLockObtained) {
            setIsLocked.on();
            return;
        }

        // Capture GA for sample manifest - amend sampling event
        trackSampleManifestHomePageAction({ action: 'amend' });

        dispatch(selectSamplingKit(''));
        navigation.toSampleManifestForm({ samplingEventId: selectedSamplingEventId, batchId: selectedBatchId });
    };

    return (
        <div className='border-orange-100 border-2 shadow-lg p-4 rounded-lg bg-white flex gap-6 justify-between'>
            <div className='flex flex-col gap-2'>
                <div className='text-orange-100 flex text-lg font-bold'>Data amendment requested by NatureMetrics</div>
                <div>
                    <p>Details of the data amendment have been emailed to the assigned project manager(s).</p>
                    <p>Please amend the data as requested and resubmit the sample batch.</p>
                </div>
                {isBatchSubmittedMoreThan5Days && (
                    <div className='bg-orange-0 border-orange-500 text-orange-100 p-4 rounded-lg' role='alert'>
                        <div className='flex flex-row gap-2'>
                            <ErrorIcon />
                            <CMSArticle slug='sample-manifest-batch-submission-more-than-5-days' />
                        </div>
                    </div>
                )}
            </div>
            <div className='flex flex-col content-end gap-2'>
                <div className='flex flex-row gap-4 justify-end'>
                    <div>
                        <Select
                            options={amendmentBatchOptions}
                            selectedValue={selectedBatchId}
                            onChange={handleSelectChange}
                            width='200px'
                        />
                    </div>
                    <div>
                        <PrimaryButton onClick={onAmendSampleBatch} disabled={!selectedBatchId}>
                            Amend sample batch &gt;
                        </PrimaryButton>
                    </div>
                </div>
                {isLocked && (
                    <div>
                        <WarningMessage message='This batch is being edited by another user, please try later.' />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AmendmentContent;
